import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import z from "zod";

import { SyncRequestClient } from "../stores/requestClient";
import type { SharingSpace } from "../types";
import { SearchParam } from "../types";
import { ServiceErrorCode } from "../utils/httpUtils";
import { buildAdvancedTypePredicateFilter, buildTypePredicateFilter } from "../utils/typeUtils";
import type { UserID } from "./useAccount";

export const ProjectUserAssignableRoles = ["editor", "commenter", "viewer"] as const;
export type ProjectUserAssignableRole = (typeof ProjectUserAssignableRoles)[number];

const ProjectUserRolesDisplayNames = {
  editor: "Editor",
  commenter: "Commenter",
  viewer: "Viewer",
} as const satisfies Record<ProjectUserAssignableRole, string>;

export const ProjectUserRoleDisplayName = (role: ProjectUserAssignableRole) => {
  return ProjectUserRolesDisplayNames[role];
};

type ProjectUserBase = {
  email: string;
  createdAt: string;
};

export type ProjectMember = ProjectUserBase & {
  userId: UserID;
  name?: string;
  role: ProjectUserAssignableRole;
  isTeamUser: boolean;
};

export const ProjectInvitationID = z.string().brand("ProjectInvitationID");
export type ProjectInvitationID = z.infer<typeof ProjectInvitationID>;

export const ProjectAccessRequestID = z.string().brand("ProjectAccessRequestID");
export type ProjectAccessRequestID = z.infer<typeof ProjectAccessRequestID>;

export type ProjectInvitation = ProjectUserBase & {
  invitationId: ProjectInvitationID;
  role: ProjectUserAssignableRole;
};

export type ProjectAccessRequest = ProjectUserBase & {
  accessRequestId: ProjectAccessRequestID;
  name?: string;
  userId: UserID;
};

export type ProjectUser = ProjectMember | ProjectInvitation | ProjectAccessRequest;

export type ProjectSharingData = {
  users: ProjectUser[];
  space?: SharingSpace;
};

export type ProjectInvitationRequestPayload = {
  emails: string[];
  role: ProjectUserAssignableRole;
};

export type ProjectInvitationResponse = {
  invitedUsers: {
    email: string;
    role: ProjectUserAssignableRole;
    userId?: UserID;
    invitationId?: ProjectInvitationID;
    name?: string;
  }[];
  errors: {
    email: string;
    code: string;
    message: string;
  }[];
};

type GetProjectInvitationResponse = {
  email?: string;
  action: "view" | "accept" | "signup" | "login";
};

export const projectSharingQueryKey = ["projectSharingQueryKey"];
export const projectInvitationQueryKey = ["projectInvitationQueryKey"];

export const isProjectInvitation = buildTypePredicateFilter<ProjectUser, ProjectInvitation>(
  "invitationId"
);

export const isProjectMember = buildAdvancedTypePredicateFilter<ProjectUser, ProjectMember>({
  required: ["userId", "role"],
});

export const isProjectAccessRequest = buildTypePredicateFilter<ProjectUser, ProjectAccessRequest>(
  "accessRequestId"
);

export const useProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const enabled = Boolean(invitationHash);

  const query = useQuery({
    queryKey: [...projectInvitationQueryKey],
    queryFn: async () => {
      const request = isLoggedIn()
        ? SyncRequestClient.authenticatedRequest
        : SyncRequestClient.publicRequest;

      const response = await request({
        method: "GET",
        url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}`,
      });
      return response.json<GetProjectInvitationResponse>();
    },
    enabled,
  });

  return { ...query, enabled };
};

export const useMutateAcceptProjectInvitation = () => {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";
  return useMutation({
    mutationFn: async ({ emailToken }: { emailToken?: string | null } = {}) => {
      try {
        return await SyncRequestClient.authenticatedRequest({
          method: "POST",
          url: `/web/projects/${projectId}/invitations-by-hash/${invitationHash}/accept`,
          body: { ...(emailToken && { emailToken }) },
        });
      } catch (error) {
        // Accept invitation is best effort.
        // Not found could mean you can view the project or could mean you can't.
        // Up to the project loading to decide and show the proper error screen.
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.NotFound) ||
            error.hasCode(ServiceErrorCode.InvitationNotFound))
        ) {
          return;
        }
        throw error;
      }
    },
  });
};

export const useProjectSharing = () => {
  const { projectId } = useParams();
  return useQuery({
    queryKey: [...projectSharingQueryKey, projectId],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/projects/${projectId}/sharing`,
      });
      return response.json<ProjectSharingData>();
    },
    select: (data) => {
      const members: ProjectMember[] = data.users.filter(isProjectMember);
      const invitations: ProjectInvitation[] = data.users.filter(isProjectInvitation);
      const accessRequests: ProjectAccessRequest[] = data.users.filter(isProjectAccessRequest);

      return { ...data, members, invitations, accessRequests };
    },
    enabled: isLoggedIn() && Boolean(projectId),
  });
};

export const useCreateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProjectInvitationResponse, variables: ProjectInvitationRequestPayload) => void;
  onError?: (error: Error, variables: ProjectInvitationRequestPayload) => void;
} = {}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...projectSharingQueryKey, projectId],
    mutationFn: async (invitations: ProjectInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/invitations`,
        body: invitations,
      });
      return response.json<ProjectInvitationResponse>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            users: [...oldData.users, ...data.invitedUsers],
          };
        }
      );
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);
    },
  });
};

export const useUpdateProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      projectInvitationId: ProjectInvitationID;
      role: ProjectUserAssignableRole;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/invitations/${data.projectInvitationId}`,
        body: {
          role: data.role,
        },
      });
      return data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);

      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as ProjectSharingData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectInvitation(user) && user.invitationId === variables.projectInvitationId) {
              return {
                ...user,
                role: variables.role,
              };
            }
            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }
      onError?.(error);
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};

export const useDeleteProjectInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProjectInvitationID, variables: { invitationId: ProjectInvitationID }) => void;
  onError?: (error: Error, variables: { invitationId: ProjectInvitationID }) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: ProjectInvitationID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/invitations/${invitationId}`,
      });
      return invitationId;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      removeInvitationFromCache(data);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project invitation", error });
      onError?.(error, variables);
    },
  });

  const removeInvitationFromCache = (invitationId: ProjectInvitationID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectInvitation(user) && user.invitationId === invitationId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeInvitationFromCache,
  };
};

export const useUpdateProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: UserID; role: ProjectUserAssignableRole }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/projects/${projectId}/users/${userId}`,
        body: {
          role: role,
        },
      });
      return { userId, role };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [...projectSharingQueryKey, projectId] });

      const previousData = queryClient.getQueryData<ProjectSharingData>([
        ...projectSharingQueryKey,
        projectId,
      ]);
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as ProjectSharingData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectMember(user) && user.userId === variables.userId) {
              return {
                ...user,
                role: variables.role,
              };
            }
            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      return { previousData };
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([...projectSharingQueryKey, projectId], context.previousData);
      }
      onError?.(error);
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};

export const useDeleteProjectUser = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: UserID, variables: { userId: UserID }) => void;
  onError?: (error: Error, variables: { userId: UserID }) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ userId }: { userId: UserID }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/users/${userId}`,
      });
      return userId;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      removeUserFromCache(data);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project user", error });
      onError?.(error, variables);
    },
  });

  const removeUserFromCache = (userId: UserID) => {
    queryClient.setQueryData(
      [...projectSharingQueryKey, projectId],
      (oldData: ProjectSharingData | undefined) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) =>
              !(
                (isProjectMember(user) && user.userId === userId) ||
                (isProjectAccessRequest(user) && user.userId === userId)
              )
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeUserFromCache,
  };
};

export const useRequestProjectAccess = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  return useMutation({
    mutationFn: async () => {
      await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/access-requests`,
      });
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      LogSentryException({ messagePrefix: "Failed to access request", error });
      onError?.(error);
    },
    retry: 1,
    retryDelay: 500,
  });
};

export const useAcceptProjectAccessRequest = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectAccessRequestId,
      role,
    }: {
      projectAccessRequestId: ProjectAccessRequestID;
      role: ProjectUserAssignableRole;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/projects/${projectId}/access-requests/${projectAccessRequestId}`,
        body: {
          role: role,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) =>
          oldData
            ? {
                ...oldData,
                users: oldData.users.map((user) => {
                  if (
                    isProjectAccessRequest(user) &&
                    user.accessRequestId === variables.projectAccessRequestId
                  ) {
                    const { accessRequestId: _drop, ...rest } = user;
                    return {
                      ...rest,
                      role: variables.role,
                    };
                  }
                  return user;
                }),
              }
            : oldData
      );
      onSuccess?.();
    },
    onError: (error) => {
      LogSentryException({ messagePrefix: "Failed to accept project request", error });
      onError?.(error);
    },
  });
};

export const useRejectProjectAccessRequest = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectAccessRequestId,
    }: {
      projectAccessRequestId: ProjectAccessRequestID;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/projects/${projectId}/access-requests/${projectAccessRequestId}`,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [...projectSharingQueryKey, projectId],
        (oldData: ProjectSharingData | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            users: oldData.users.filter(
              (user) =>
                !(
                  isProjectAccessRequest(user) &&
                  user.accessRequestId === variables.projectAccessRequestId
                )
            ),
          };
        }
      );
      onSuccess?.();
    },
    onError: (error) => {
      LogSentryException({ messagePrefix: "Failed to reject project access request", error });
      onError?.(error);
    },
  });
};
